import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import {
  Segmente, Icone, STATUTS, TYPES, libelleType, libelleEchange, libelleStatut, dateCourte, dateJour, quandRelance,
  messageErreur, useEstMobile, aller,
} from './commun';
import Noter from './Noter';

const telLien = (t) => `tel:${String(t).replace(/[^\d+]/g, '')}`;
const itineraire = (c) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([c.nom, c.adresse, c.code_postal, c.ville].filter(Boolean).join(' '))}`;

function Coordonnees({ compte, utilisateur, referentiel, onFerme, onFini }) {
  const [f, setF] = useState({
    nom: compte.nom || '', type: compte.type, ville: compte.ville || '', code_postal: compte.code_postal || '',
    adresse: compte.adresse || '', contact_nom: compte.contact_nom || '', contact_fonction: compte.contact_fonction || '',
    telephone: compte.telephone || '', email: compte.email || '', origine: compte.origine || '', code_erp: compte.code_erp || '',
    notes: compte.notes || '', commercial_id: compte.commercial_id ? String(compte.commercial_id) : '', societe_id: String(compte.societe_id),
  });
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const maj = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const multi = !utilisateur.societe_id && (referentiel?.societes.length || 0) > 1;
  const commerciaux = (referentiel?.utilisateurs || []).filter((u) => !u.societe_id || String(u.societe_id) === f.societe_id);

  const enregistrer = async (e) => {
    e.preventDefault(); setEnvoi(true); setErreur('');
    try {
      const corps = { ...f, commercial_id: f.commercial_id || null };
      if (!multi) delete corps.societe_id;
      await api.patch(`/commercial/comptes/${compte.id}`, corps);
      onFini();
    } catch (err) { setErreur(messageErreur(err)); } finally { setEnvoi(false); }
  };
  const supprimer = async () => {
    if (!window.confirm(`Supprimer définitivement « ${compte.nom} » et tout son historique ?\n\nPour un prospect qui ne donne rien, préférez le statut « Perdu » : il garde son historique.`)) return;
    try { await api.delete(`/commercial/comptes/${compte.id}`); aller('#/comptes'); } catch (err) { setErreur(messageErreur(err)); }
  };

  return (
    <div className="voile" role="dialog" aria-modal="true" aria-labelledby="titre-coord">
      <form className="modale modale-large" onSubmit={enregistrer}>
        <div className="modale-entete">
          <h2 id="titre-coord">Coordonnées</h2>
          <button type="button" className="bouton-icone" onClick={onFerme} aria-label="Fermer"><Icone.Fermer /></button>
        </div>
        <div className="formulaire-grille">
          <label className="large"><span>Nom de l'établissement *</span><input value={f.nom} onChange={maj('nom')} required /></label>
          <label><span>Type</span>
            <select value={f.type} onChange={maj('type')}>{TYPES.map((t) => <option key={t.cle} value={t.cle}>{t.libelle}</option>)}</select>
          </label>
          <label><span>Code client ERP</span><input value={f.code_erp} onChange={maj('code_erp')} placeholder="Code ISAVIGNES" /></label>
          <label className="large"><span>Adresse</span><input value={f.adresse} onChange={maj('adresse')} /></label>
          <label><span>Code postal</span><input value={f.code_postal} onChange={maj('code_postal')} inputMode="numeric" /></label>
          <label><span>Ville *</span><input value={f.ville} onChange={maj('ville')} required /></label>
          <label><span>Contact</span><input value={f.contact_nom} onChange={maj('contact_nom')} /></label>
          <label><span>Fonction</span><input value={f.contact_fonction} onChange={maj('contact_fonction')} placeholder="Gérant, chef, acheteur…" /></label>
          <label><span>Téléphone</span><input value={f.telephone} onChange={maj('telephone')} type="tel" /></label>
          <label><span>E-mail</span><input value={f.email} onChange={maj('email')} type="email" /></label>
          <label><span>Origine</span><input value={f.origine} onChange={maj('origine')} placeholder="Salon, recommandation, site…" /></label>
          {multi && (
            <label><span>Société</span>
              <select value={f.societe_id} onChange={(e) => setF({ ...f, societe_id: e.target.value, commercial_id: '' })}>
                {referentiel.societes.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
              </select>
            </label>
          )}
          <label><span>Commercial</span>
            <select value={f.commercial_id} onChange={maj('commercial_id')}>
              <option value="">Personne</option>
              {commerciaux.map((u) => <option key={u.id} value={u.id}>{u.nom || u.identifiant}</option>)}
            </select>
          </label>
          <label className="large"><span>Notes</span><textarea rows={3} value={f.notes} onChange={maj('notes')} placeholder="Ce qu'il faut savoir sur ce compte : jours de livraison, gamme recherchée…" /></label>
        </div>
        {erreur && <div className="erreur">{erreur}</div>}
        <div className="actions">
          {utilisateur.role === 'admin' && <button type="button" className="danger pousse-gauche" onClick={supprimer}>Supprimer le compte</button>}
          <button type="button" className="lien" onClick={onFerme}>Annuler</button>
          <button type="submit" disabled={envoi}>{envoi ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </form>
    </div>
  );
}

export default function Fiche({ id, params, utilisateur, referentiel }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  const [edition, setEdition] = useState(false);
  const [saisie, setSaisie] = useState(params.get('noter') === '1');
  const mobile = useEstMobile();
  const depuisRelances = params.get('noter') === '1';

  const charger = useCallback(async () => {
    try { setD((await api.get(`/commercial/comptes/${id}`)).data); setErreur(''); }
    catch (e) { setErreur(messageErreur(e, 'Compte introuvable.')); }
  }, [id]);
  useEffect(() => { charger(); }, [charger]);

  const changerStatut = async (s) => {
    if (s === d.compte.statut) return;
    if (s === 'perdu' && !window.confirm('Passer ce compte en « Perdu » ?\n\nSa relance prévue est annulée et il sort de « Mes relances ». Son historique est conservé.')) return;
    try { await api.patch(`/commercial/comptes/${id}`, { statut: s }); charger(); }
    catch (e) { alert(messageErreur(e)); }
  };
  const supprimerEchange = async (h) => {
    if (!window.confirm('Supprimer cet échange ?')) return;
    try { await api.delete(`/commercial/echanges/${h.id}`); charger(); } catch (e) { alert(messageErreur(e)); }
  };
  const apresSaisie = () => {
    // Venu de « Mes relances » sur téléphone : on y retourne, comme dans la maquette.
    if (mobile && depuisRelances && window.history.length > 1) { window.history.back(); return; }
    setSaisie(false); charger();
  };

  if (erreur) return <div className="alerte">{erreur} <a href="#/comptes">Retour aux comptes</a></div>;
  if (!d) return <div className="attente">Chargement…</div>;
  const c = d.compte;
  const q = quandRelance(c.prochaine_action_le, c.dans_jours);
  const afficherSaisie = !mobile || saisie;

  return (
    <div className="fiche">
      <div className="fil-ariane cache-mobile"><a href="#/comptes">Comptes</a> › {c.nom}</div>
      <button type="button" className="retour-mobile" onClick={() => window.history.back()}><Icone.Retour /> Retour</button>

      <div className="fiche-entete carte">
        <div className="fiche-identite">
          <h1>{c.nom}</h1>
          <p className="sous-titre">
            {libelleType(c.type)}{c.adresse ? ` · ${c.adresse}` : ''}{c.ville ? `, ${c.code_postal ? c.code_postal + ' ' : ''}${c.ville}` : ''}
            {c.contact_nom ? ` · ${c.contact_nom}${c.contact_fonction ? `, ${c.contact_fonction}` : ''}` : ''}
          </p>
          <div className="fiche-contacts">
            {c.telephone && <a className="bouton-blanc contact-tel" href={telLien(c.telephone)}><Icone.Tel taille={16} /> <span>{c.telephone}</span></a>}
            {c.email && <a className="bouton-blanc" href={`mailto:${c.email}`}><Icone.Mail taille={16} /> <span>{mobile ? 'E-mail' : c.email}</span></a>}
            {(c.adresse || c.ville) && <a className="bouton-blanc" href={itineraire(c)} target="_blank" rel="noreferrer"><Icone.Lieu taille={16} /> <span>Itinéraire</span></a>}
            <button type="button" className="bouton-blanc" onClick={() => setEdition(true)}><Icone.Crayon taille={16} /> <span>Modifier</span></button>
          </div>
        </div>
        <div className="fiche-statut">
          <span className="etiquette">Statut · un clic pour changer</span>
          <Segmente etiquette="Statut du compte" valeur={c.statut} onChange={changerStatut} className="segmente-statut"
            options={STATUTS.map((s) => ({ cle: s.cle, libelle: s.cle === 'client' ? 'Client signé' : s.libelle, classe: `seg-${s.cle}` }))} />
        </div>
      </div>

      <div className="fiche-grille">
        <section className="fiche-principal">
          {mobile && (
            <div className={`prochaine carte ${q.retard ? 'en-retard' : ''}`}>
              <span className="etiquette-forte">Prochaine action</span>
              <strong>{c.prochaine_action_le ? (c.prochaine_action || 'Relancer') : 'Aucune relance prévue'}</strong>
              {c.prochaine_action_le && <span className={q.retard ? 'rouge gras' : 'discret'}>{q.retard ? q.texte : `Prévue ${c.dans_jours === 0 ? "aujourd'hui" : 'le ' + dateJour(c.prochaine_action_le)}`}</span>}
            </div>
          )}
          {mobile && !saisie && (
            <button type="button" className="bouton-grand bouton-pleine-largeur" onClick={() => setSaisie(true)}><Icone.Crayon /> Noter un échange</button>
          )}
          {afficherSaisie && <Noter compte={c} onEnregistre={apresSaisie} onAnnule={mobile ? () => setSaisie(false) : null} autoFocus={mobile && saisie} />}

          <div className="historique carte">
            <h2>Historique</h2>
            {d.historique.length === 0 && <p className="discret">Aucun échange pour l'instant.</p>}
            {d.historique.map((h) => (
              <div key={`${h.genre}-${h.id}`} className={`histo histo-${h.genre}`}>
                <span className="histo-date">{dateCourte(h.le)}</span>
                <span className="histo-type">{h.genre === 'statut' ? 'Statut' : libelleEchange(h.type)}</span>
                <span className="histo-note">
                  {h.genre === 'statut'
                    ? <>{h.de ? `${libelleStatut(h.de)} → ` : ''}<strong>{libelleStatut(h.vers)}</strong></>
                    : (h.note || <span className="discret">Sans commentaire</span>)}
                  {h.auteur_nom && <span className="histo-auteur"> · {h.auteur_nom}</span>}
                </span>
                {h.genre === 'echange' && (h.auteur_id === utilisateur.id || utilisateur.role === 'admin') && (
                  <button type="button" className="bouton-icone petit" onClick={() => supprimerEchange(h)} aria-label="Supprimer cet échange"><Icone.Fermer taille={14} /></button>
                )}
              </div>
            ))}
            <div className="histo histo-creation">
              <span className="histo-date">{dateCourte(c.created_at)}</span>
              <span className="histo-type">Création</span>
              <span className="histo-note">Compte créé{c.cree_par_nom ? ` par ${c.cree_par_nom}` : ''}{c.origine ? ` · ${c.origine}` : ''}</span>
            </div>
          </div>
        </section>

        <aside className="fiche-cote">
          {!mobile && (
            <div className={`prochaine carte ${q.retard ? 'en-retard' : ''}`}>
              <span className="etiquette-forte">Prochaine action</span>
              <strong>{c.prochaine_action_le ? (c.prochaine_action || 'Relancer') : 'Aucune relance prévue'}</strong>
              {c.prochaine_action_le && <span className={q.retard ? 'rouge gras' : 'discret'}>{q.retard ? q.texte : `Prévue ${c.dans_jours === 0 ? "aujourd'hui" : 'le ' + dateJour(c.prochaine_action_le)}`}</span>}
              {!c.prochaine_action_le && ['prospect', 'discussion'].includes(c.statut) && <span className="discret">Notez un échange pour fixer la prochaine relance.</span>}
            </div>
          )}
          <div className="carte infos">
            <span>Société</span><span>{c.societe_nom}</span>
            <span>Commercial</span><span>{c.commercial_nom || <span className="discret">Personne</span>}</span>
            {c.origine && <><span>Origine</span><span>{c.origine}</span></>}
            <span>Créé le</span><span>{dateCourte(c.created_at)}</span>
            {c.signe_le && <><span>Signé le</span><span>{dateCourte(c.signe_le)}</span></>}
            <span>Code ERP</span><span>{c.code_erp || <span className="discret">{c.statut === 'client' ? 'à renseigner' : 'à la signature'}</span>}</span>
          </div>
          {c.notes && <div className="carte notes"><span className="etiquette">Notes</span><p>{c.notes}</p></div>}
        </aside>
      </div>

      {edition && <Coordonnees compte={c} utilisateur={utilisateur} referentiel={referentiel} onFerme={() => setEdition(false)} onFini={() => { setEdition(false); charger(); }} />}
    </div>
  );
}
